// Top bar + main content wrapper for authenticated pages.
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Brand } from "./Brand";
import { useAuth } from "../lib/AuthContext";
import { DebugPanel } from "./DebugPanel";
import clsx from "clsx";
import { useEffect, useState, type ReactNode } from "react";

function UserMenu({ initials, email, onSignOut }: { initials: string; email: string | null; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className="w-10 h-10 rounded-full bg-paper border border-hairline-bold text-ink-body font-serif text-[17px] leading-none flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors"
        title={email ?? "Account"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initials}
      </button>
      {open ? (
        <div
          onMouseLeave={() => setOpen(false)}
          className="absolute right-0 mt-2 w-72 rounded-md border border-hairline-bold bg-paper shadow-[0_12px_28px_-10px_rgba(45,28,20,0.2)] z-20"
          role="menu"
        >
          {email ? (
            <div className="px-5 py-4 border-b border-hairline">
              <div className="font-mono text-[11px] tracking-smallcaps uppercase text-ink-dim">Signed in as</div>
              <div className="mt-1 font-body text-[15px] text-ink-body truncate" title={email}>{email}</div>
            </div>
          ) : null}
          <Link
            to="/my-books"
            onClick={() => setOpen(false)}
            className="block px-5 py-3 font-body text-[15px] text-ink-body hover:bg-parchment"
            role="menuitem"
          >
            My Books
          </Link>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-5 py-3 font-body text-[15px] text-ink-body hover:bg-parchment"
            role="menuitem"
          >
            Account &amp; billing
          </Link>
          <Link
            to="/pricing"
            onClick={() => setOpen(false)}
            className="block px-5 py-3 font-body text-[15px] text-ink-body hover:bg-parchment"
            role="menuitem"
          >
            Pricing
          </Link>
          <button
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full text-left px-5 py-3 font-body text-[15px] text-wax hover:bg-parchment border-t border-hairline"
            role="menuitem"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NavItem({ to, label, primary = false, stamp = false }: { to: string; label: string; primary?: boolean; stamp?: boolean }) {
  // Three nav-item registers:
  //
  //   - default → italic Cormorant + terracotta foil-rule when active
  //     (Library, etc.) - the everyday public surface.
  //   - primary → italic Cormorant + foil-gold ★ prefix, larger, no box
  //     (Build a book) - the hero CTA. Star is intentional and
  //     preserved across the redesign.
  //   - stamp   → mono uppercase tracking-stamp inside a foil-bordered
  //     pill (The Engine) - signals "the machinery", visually
  //     distinct from the two italic primary tabs without competing
  //     with them.
  //
  // The brand wordmark has its own ❦-flank treatment in the footer, so
  // brand · CTA · selected · stamp are all visually separable.
  if (stamp) {
    return (
      <NavLink
        to={to}
        end
        className={({ isActive }) =>
          clsx(
            "font-mono uppercase tracking-stamp text-[10.5px] leading-none rounded-sm border px-2 py-1 transition-colors duration-200 ease-observatory whitespace-nowrap",
            isActive
              ? "text-terracotta border-terracotta/60 bg-terracotta/5"
              : "text-ink-faint border-foil/40 hover:text-terracotta hover:border-terracotta/50",
          )
        }
      >
        {label}
      </NavLink>
    );
  }
  if (primary) {
    return (
      <NavLink
        to={to}
        end
        className={({ isActive }) =>
          clsx(
            "relative font-serif italic leading-none transition-colors duration-200 ease-observatory",
            "text-[20px] sm:text-[21px] font-medium tracking-[-0.005em]",
            isActive ? "text-terracotta" : "text-terracotta hover:text-wax",
          )
        }
      >
        <span aria-hidden className="text-terracotta text-[0.78em] not-italic mr-1.5">★</span>
        <span>{label}</span>
      </NavLink>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        clsx(
          "relative font-serif italic text-[19px] sm:text-[20px] leading-none transition-colors duration-200 ease-observatory",
          isActive
            ? "text-terracotta"
            : "text-ink-body/85 hover:text-terracotta",
        )
      }
    >
      {({ isActive }) => (
        <span className="relative inline-flex items-center">
          <span className="tracking-[-0.005em]">{label}</span>
          {/* Absolute so the rule never adds height: an in-flow underline makes
              the item box taller than its text and pushes the label above the
              top bar's optical centre line. */}
          <span
            className={clsx(
              "absolute left-0 -bottom-1.5 h-[1.5px] bg-terracotta transition-[width,opacity] duration-300 ease-observatory",
              isActive
                ? "w-full opacity-100"
                : "w-0 opacity-0 group-hover:w-full group-hover:opacity-80",
            )}
          />
        </span>
      )}
    </NavLink>
  );
}

/**
 * UtilityLink - right-side nav register for personal/account surfaces.
 * Serif italic, sized to match the rest of the top bar (Sign in + primary
 * nav) so the whole header reads as one cohesive type register. Ink-dim by
 * default, terracotta on hover/active. No glyph icon: the emoji read as
 * out-of-place against the literary serif chrome.
 */
function UtilityLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        clsx(
          "hidden sm:inline-flex items-center font-serif italic text-[15px] sm:text-[16px] leading-none transition-colors duration-200 ease-observatory whitespace-nowrap",
          isActive ? "text-terracotta" : "text-ink-dim hover:text-terracotta",
        )
      }
    >
      {label}
    </NavLink>
  );
}

/**
 * FooterCol - a column of footer links in the brand's serif register under
 * a mono small-caps eyebrow. Keeps the footer type cohesive with the rest
 * of the chrome: serif for content/links, mono small-caps for labels.
 */
function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div>
      <div className="eyebrow mb-3">{title}</div>
      <ul className="space-y-2">
        {links.map(([label, to]) => (
          <li key={to + label}>
            <Link
              to={to}
              className="font-serif text-[15px] text-ink-body/85 hover:text-terracotta transition-colors duration-200 ease-observatory"
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}


export function Shell({
  children,
  fullHeight = false,
  hideFooter = false,
  compactMobile = false,
}: {
  children: ReactNode;
  /** Lock the app to exactly one viewport height and hand scrolling to the
   *  page's own content (used by the studio's fixed-rail work surface). */
  fullHeight?: boolean;
  /** Suppress the marketing footer on focused work surfaces (e.g. the studio). */
  hideFooter?: boolean;
  /** Keep focused mock/work surfaces within the mobile viewport by collapsing
   *  the full navigation below the `sm` breakpoint. Desktop chrome is unchanged. */
  compactMobile?: boolean;
}) {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name ?? user?.email ?? "?").slice(0, 1).toUpperCase();
  const year = new Date().getFullYear();
  const accountLinks: [string, string][] = user
    ? [["My Books", "/my-books"], ["Account & billing", "/account"]]
    : [["Sign in", "/login"], ["Create account", "/register"]];

  useEffect(() => {
    if (!fullHeight) return;

    const htmlOverflow = document.documentElement.style.overflow;
    const bodyOverflow = document.body.style.overflow;
    const htmlOverscroll = document.documentElement.style.overscrollBehavior;
    const bodyOverscroll = document.body.style.overscrollBehavior;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overscrollBehavior = "none";
    document.body.style.overscrollBehavior = "none";

    return () => {
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overscrollBehavior = htmlOverscroll;
      document.body.style.overscrollBehavior = bodyOverscroll;
    };
  }, [fullHeight]);

  return (
    <div
      className={clsx(
        "grid",
        fullHeight ? "h-[100dvh] min-h-0 overflow-hidden overscroll-none" : "min-h-screen",
        hideFooter ? "grid-rows-[auto_1fr]" : "grid-rows-[auto_1fr_auto]",
      )}
    >
      <header
        className={clsx(
          "sticky top-0 z-40 bg-petal/95 backdrop-blur-sm flex items-center justify-between gap-3 py-5 sm:py-3.5 border-b border-hairline-bold",
          fullHeight ? "px-6" : "px-4 sm:px-8 lg:px-8",
        )}
      >
        <div className="flex items-center gap-4 sm:gap-8 lg:gap-12 min-w-0">
          <Link to="/" className="shrink-0"><Brand /></Link>
          {/* v5 (owner 2026-07-22) — Marketplace + The Engine tabs removed
              from the main nav (their routes stay live); Workshop is now
              "Writer's Room". */}
          {/* v5.1 (owner 2026-07-23) — order is Build a book · Library ·
              Writer's Room: the hero CTA leads. */}
          <nav className={clsx(
            "items-center gap-4 sm:gap-6 lg:gap-8 overflow-x-auto no-scrollbar",
            compactMobile ? "hidden sm:flex" : "flex",
          )}>
            <NavItem to="/dreambook" label="Build a book" primary />
            <NavItem to="/library" label="Library" />
            <NavItem to="/workshop" label="Writer's Room" />
          </nav>
        </div>
        <div className={clsx(
          "items-center gap-4 sm:gap-6 lg:gap-8 text-[13px] text-ink-dim shrink-0",
          compactMobile ? "hidden sm:flex" : "flex",
        )}>
          {user?.role === "admin" ? <UtilityLink to="/admin" label="Admin" /> : null}
          {/* Pricing now reads in the main-nav register (owner 2026-07-23) —
              My Books moved into the profile menu; the DEV/STAGE badge and the
              version stamp are gone from the bar (version lives in the wizard's
              step rail now). */}
          <NavItem to="/pricing" label="Pricing" />
          {user ? (
            <UserMenu initials={initials} email={user?.email ?? null} onSignOut={() => { signOut(); nav("/login"); }} />
          ) : (
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <Link
                to="/login"
                className="font-serif italic text-[14px] sm:text-[15px] text-ink-body/85 hover:text-terracotta transition-colors duration-200 ease-observatory whitespace-nowrap"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="font-mono uppercase tracking-smallcaps text-[10.5px] leading-none rounded-sm border border-terracotta bg-terracotta text-paper px-3 py-2 hover:bg-wax hover:border-wax transition-colors whitespace-nowrap"
              >
                Create account
              </Link>
            </div>
          )}
        </div>
      </header>

      <main className={clsx(fullHeight && "min-h-0 overflow-hidden")}>{children}</main>

      {user?.role === "admin" ? <DebugPanel /> : null}

      {!hideFooter && (
      <footer className="relative border-t border-hairline-bold bg-paper-deep/40 px-4 sm:px-8 lg:px-12 pt-12 pb-8">
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-petal px-3 text-terracotta text-lg" aria-hidden>❦</span>
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col gap-10 md:flex-row md:justify-between">
            {/* Colophon wordmark + tagline - the brand appears in full only
                here, reading as the book's imprint line. */}
            <div className="max-w-xs">
              <Brand size="sm" tagline />
              <p className="mt-4 font-body italic text-[13.5px] leading-relaxed text-ink-dim">
                An engine that drafts, critiques, and revises a full-length book with you, then a real editor to finish it.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-x-10 gap-y-7 sm:grid-cols-4">
              <FooterCol title="Create" links={[["Build a book", "/dreambook"], ["How the engine works", "/db-engine"], ["Pricing & credits", "/pricing"]]} />
              <FooterCol title="Read" links={[["Library", "/library"], ["Marketplace", "/marketplace"], ["Writer's Room", "/workshop"]]} />
              <FooterCol title="Account" links={accountLinks} />
              <FooterCol title="Company" links={[["About", "/about"], ["Contact", "/contact"]]} />
            </div>
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-hairline pt-6 sm:flex-row">
            <span className="font-mono text-[10.5px] tracking-smallcaps uppercase text-ink-dim">© {year} DreamBook &middot; MerakiLabs</span>
            <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <Link to="/terms" className="font-mono text-[10.5px] tracking-smallcaps uppercase text-ink-dim hover:text-terracotta transition-colors">Terms</Link>
              <Link to="/privacy" className="font-mono text-[10.5px] tracking-smallcaps uppercase text-ink-dim hover:text-terracotta transition-colors">Privacy</Link>
              <Link to="/refunds" className="font-mono text-[10.5px] tracking-smallcaps uppercase text-ink-dim hover:text-terracotta transition-colors">Refunds</Link>
            </nav>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}
